import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { weatherService } from '../../../services/weatherService';

const WeatherWidget = ({ weather, isLoading, spotName, coordinates }) => {
  const [forecast, setForecast] = useState([]);
  const [isLoadingForecast, setIsLoadingForecast] = useState(false);

  // Fetch 3-day forecast when coordinates are available
  useEffect(() => {
    const fetchForecast = async () => {
      if (!coordinates?.lat || !coordinates?.lng) return;

      try {
        setIsLoadingForecast(true);
        const forecastData = [];
        
        // Get forecast for next 3 days
        for (let i = 1; i <= 3; i++) {
          const futureDate = new Date();
          futureDate?.setDate(futureDate?.getDate() + i);
          const dateStr = futureDate?.toISOString()?.split('T')?.[0];
          
          const dayForecast = await weatherService?.getForecastForEventDate(
            coordinates?.lat,
            coordinates?.lng,
            dateStr
          );
          
          if (dayForecast && !dayForecast?.tooFarAway && !dayForecast?.isPast) {
            forecastData?.push({
              ...dayForecast,
              dayName: getDayName(i)
            });
          }
        }
        
        setForecast(forecastData);
      } catch (err) {
        console.error('Error fetching forecast:', err);
      } finally {
        setIsLoadingForecast(false);
      }
    };

    fetchForecast();
  }, [coordinates]);

  const getDayName = (daysAhead) => {
    const days = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];
    const date = new Date();
    date?.setDate(date?.getDate() + daysAhead);
    return days?.[date?.getDay()];
  };

  const getWaveColor = (height) => {
    if (height < 0.5) return 'text-green-600 dark:text-green-400';
    if (height < 1.0) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  };

  // Get SUP suitability badge
  const suitability = weatherService?.getSUPSuitability(weather);

  // Loading state
  if (isLoading) {
    return (
      <div className="bg-card rounded-xl md:rounded-2xl p-4 md:p-6 border border-border">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  // Error/No data state
  if (!weather) {
    return (
      <div className="bg-card rounded-xl md:rounded-2xl p-4 md:p-6 border border-border">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Icon name="CloudSun" size={24} className="text-primary" />
            <h3 className="text-lg md:text-xl font-semibold text-foreground">
              Hava Durumu
            </h3>
          </div>
        </div>
        <div className="text-center py-4">
          <Icon name="CloudOff" size={32} className="text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">Hava durumu verisi alınamadı</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl md:rounded-2xl p-4 md:p-6 border border-border">
      {/* Header with Location Indicator */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Icon name="CloudSun" size={24} className="text-primary" />
          <h3 className="text-lg md:text-xl font-semibold text-foreground">
            Hava Durumu
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <Icon name="MapPin" size={16} className="text-muted-foreground" />
          <span className="text-xs md:text-sm text-muted-foreground">
            {spotName || 'Bu Konum'}
          </span>
        </div>
      </div>
      {/* Current Weather Status Badge */}
      <div className="mb-4">
        <span className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium ${suitability?.color}`}>
          <Icon name="Waves" size={16} />
          {suitability?.text}
        </span>
      </div>
      {/* Location Info */}
      <div className="mb-4 p-3 bg-muted/50 rounded-lg">
        <p className="text-sm text-muted-foreground flex items-center gap-2">
          <Icon name="Info" size={16} className="flex-shrink-0" />
          <span>Bu spot için şu anki güncel hava durumu bilgileri</span>
        </p>
      </div>
      {/* Current Weather Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
          <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
            <Icon name="Thermometer" size={24} className="text-primary" />
          </div>
          <div>
            <p className="text-xs md:text-sm text-muted-foreground">Sıcaklık</p>
            <p className="text-lg md:text-xl font-semibold text-foreground">
              {weather?.temp}°C
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
          <div className="w-12 h-12 bg-secondary/10 rounded-full flex items-center justify-center flex-shrink-0">
            <Icon name="Wind" size={24} className="text-secondary" />
          </div>
          <div>
            <p className="text-xs md:text-sm text-muted-foreground">Rüzgar Hızı</p>
            <p className="text-lg md:text-xl font-semibold text-foreground">
              {weather?.windSpeed} km/s
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
          <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center flex-shrink-0">
            <Icon name="Waves" size={24} className="text-accent" />
          </div>
          <div>
            <p className="text-xs md:text-sm text-muted-foreground">Dalga Yüksekliği</p>
            <p className={`text-lg md:text-xl font-semibold ${getWaveColor(weather?.waveHeight)}`}>
              {weather?.waveHeight}m
            </p>
          </div>
        </div>
      </div>
      {/* Wind Direction */}
      <div className="mb-6 p-3 bg-muted rounded-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon name="Compass" size={20} className="text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Rüzgar Yönü</span>
          </div>
          <span className="text-lg font-semibold text-foreground">{weather?.windDirection}°</span>
        </div>
      </div>
      {/* Safety Warnings */}
      {suitability?.level === 'dangerous' && (
        <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <Icon name="AlertTriangle" size={16} className="text-red-600 dark:text-red-400 mt-0.5" />
            <p className="text-xs text-red-800 dark:text-red-300">
              Dikkat: Hava koşulları SUP için uygun değil. Deneyimli kullanıcılar için bile riskli olabilir.
            </p>
          </div>
        </div>
      )}
      {suitability?.level === 'perfect' && (
        <div className="mb-6 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <Icon name="CheckCircle" size={16} className="text-green-600 dark:text-green-400 mt-0.5" />
            <p className="text-xs text-green-800 dark:text-green-300">
              Harika! Bugün SUP için mükemmel koşullar var.
            </p>
          </div>
        </div>
      )}
      {/* 3-Day Forecast Section */}
      <div className="border-t border-border pt-4">
        <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
          <Icon name="Calendar" size={18} />
          3 Günlük Tahmin
        </h4>
        
        {isLoadingForecast ? (
          <div className="flex items-center justify-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          </div>
        ) : forecast?.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {forecast?.map((day, index) => (
              <div key={index} className="bg-muted/50 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-foreground">{day?.dayName}</span>
                  <Icon 
                    name={day?.condition?.iconName || 'Cloud'} 
                    size={20} 
                    className="text-primary" 
                  />
                </div>
                <div className="space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Sıcaklık</span>
                    <span className="font-semibold text-foreground">
                      {day?.temperature?.max}° / {day?.temperature?.min}°
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Rüzgar</span>
                    <span className="font-semibold text-foreground">{day?.windSpeed} km/s</span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {day?.condition?.description}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground text-center py-3">
            Tahmin verisi yüklenemedi
          </p>
        )}
      </div>
    </div>
  );
};

export default WeatherWidget;